import React from 'react';
import ResponsiveECharts from './ResponsiveECharts';
import { baseOption, axeValeur, TAILLE_TEXTE, COULEUR_TEXTE } from './echartsTheme';
import { ajouterJours, formatCourt, formatLong, formatNombre, isoDepuisTs } from '../utils/eau3mois';

// Couleurs des postes : maïs, vaches et porcs reprennent celles du front (graphique
// Tension) ; tourisme et résidents ont les leurs.
const COULEURS_POSTES = {
  'Maïs (irrigation)': '#92D050',
  'Maïs (besoin en eau)': '#92D050',
  'Vaches laitières': '#6C2B72',
  'Porcs engraissement': '#FFB7F9',
  'Tourisme': '#C0C0C0',
  'Résidents': '#C8D9F1',
};
const LIGNES = [
  { cle: 'min', libelle: 'minimum du total', type: 'dashed' },
  { cle: 'med', libelle: 'médiane du total', type: 'dotted' },
];

/**
 * Consommation du territoire, toutes sources : aires empilées par poste
 * (MAXIMUM de chaque poste sur l'ensemble des trajectoires) et, en lignes, le
 * minimum et la médiane du total sommé trajectoire par trajectoire.
 *
 * @param postes   noms des postes, du HAUT de l'empilement vers le socle
 * @param parPoste { [poste]: valeurs }
 * @param total    { min, med, max }
 * @param unite    libellé de l'axe Y ("m³/jour" ou "millions de m³")
 * @param decimales décimales affichées (0 en m³/jour, 1 en millions de m³)
 */
export default function Eau3MoisTotalChart({ origine, postes, parPoste, total, coupure, unite, decimales = 0 }) {
  const n = total.med.length;
  const dates = Array.from({ length: n }, (_, i) => ajouterJours(origine, i));
  const paires = (valeurs) => valeurs.map((v, i) => [dates[i], v]);

  // ECharts empile dans l'ordre des séries (la première est en bas) : le Shiny
  // met le premier poste en haut et le dernier au socle, d'où l'inversion.
  const series = [...postes].reverse().map((p, i) => ({
    name: p, type: 'line', stack: 'postes', symbol: 'none',
    lineStyle: { width: 0.6, color: '#fff' }, itemStyle: { color: COULEURS_POSTES[p] ?? '#888' },
    areaStyle: { color: COULEURS_POSTES[p] ?? '#888', opacity: 0.95 }, data: paires(parPoste[p]),
    ...(i === 0 ? {
      markLine: { silent: true, symbol: 'none', label: { show: false },
        lineStyle: { type: 'dashed', color: '#595959' }, data: [{ xAxis: coupure }] },
    } : {}),
  }));
  LIGNES.forEach((l) => series.push({
    name: l.libelle, type: 'line', symbol: 'none',
    lineStyle: { color: '#262626', width: 1.8, type: l.type }, itemStyle: { color: '#262626' },
    data: paires(total[l.cle]),
  }));

  const option = {
    ...baseOption(),
    grid: { left: 12, right: 24, bottom: 78, top: 56, containLabel: true },
    legend: {
      bottom: 0, itemGap: 16, textStyle: { fontSize: TAILLE_TEXTE, color: COULEUR_TEXTE },
      data: [...postes, ...LIGNES.map((l) => l.libelle)],
    },
    tooltip: {
      trigger: 'axis',
      textStyle: { fontSize: TAILLE_TEXTE, color: COULEUR_TEXTE },
      formatter: (params) => {
        const lignes = [`<b>${formatLong(isoDepuisTs(params[0].axisValue))}</b>`];
        [...params].reverse().forEach((p) => {
          lignes.push(`${p.marker} ${p.seriesName} : ${formatNombre(p.value[1], decimales)} ${unite}`);
        });
        return lignes.join('<br/>');
      },
    },
    xAxis: {
      type: 'time',
      axisLabel: { fontSize: TAILLE_TEXTE, color: COULEUR_TEXTE, hideOverlap: true,
        formatter: (v) => formatCourt(isoDepuisTs(v)) },
      axisLine: { lineStyle: { color: COULEUR_TEXTE } },
    },
    yAxis: axeValeur({ nom: unite, formatteur: (v) => formatNombre(v, 0) }),
    series,
  };

  return <ResponsiveECharts option={option} style={{ height: 540 }} />;
}
